import {useApolloClient} from "@apollo/client";
import {useState} from "react";
import {FormattedButton, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {getRecordListInterval, IntervalWidget, intervals} from "@/components/IntervalWidget";
import {downloadRecords} from "@/components/DownloadRecordsCsv";
import {FaxRecordListQuery, FaxRecordListQueryVariables, FaxRecordListDocument} from "../queries.generated";
import {FormattedMessage, defineMessages, defineMessage} from "react-intl";
import {FaxDirection, FaxStatus} from "@/api";
import {RecordList} from "@/components/RecordList";
import {FaxRecord} from "@/components/fax/records/types";
import {DateTime} from "luxon";
import {KeyString} from "@/tools";
import {DirectionWidget} from "@/components/fax/records/DirectionWidget";

const defaultFieldNames: KeyString<FaxRecord>[] = ["time", "remote", "direction", "status", "pages", "subject"];

// Static message descriptors for fax directions
const FAX_DIRECTION_MESSAGES = defineMessages<FaxDirection>({
    ALL: {id: "faxRecordsList.directions.ALL", defaultMessage: "All"},
    INCOMING: {id: "faxRecordsList.directions.INCOMING", defaultMessage: "Incoming"},
    OUTGOING: {id: "faxRecordsList.directions.OUTGOING", defaultMessage: "Outgoing"},
});

// Static message descriptors for fax statuses
const FAX_STATUS_MESSAGES = defineMessages<FaxStatus>({
    ERROR: {id: "faxRecordsList.statuses.ERROR", defaultMessage: "Failed"},
    SENT: {id: "faxRecordsList.statuses.SENT", defaultMessage: "Successful"},
    UNDER_PROCESSING: {id: "faxRecordsList.statuses.UNDER_PROCESSING", defaultMessage: "Under processing"},
});

const fieldContentGenerators: {[K in keyof FaxRecord]: (record: FaxRecord) => any} = {
    time: (record: FaxRecord) => DateTime.fromISO(record.time).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS),
    remote: (record: FaxRecord) => record.remote,
    direction: (record: FaxRecord) => <FormattedMessage {...FAX_DIRECTION_MESSAGES[record.direction]} />,
    status: (record: FaxRecord) => <FormattedMessage {...FAX_STATUS_MESSAGES[record.status]} />,
    pages: (record: FaxRecord) => record.pages,
    subject: (record: FaxRecord) => record.subject,
};

export const FaxRecordsPage = ({faxId}: {faxId: number}) => {
    const [recordList, setRecordList] = useState<FaxRecord[]>(null);
    const [interval, setInterval] = useState<intervals>("thisMonth");
    const [direction, setDirection] = useState<FaxDirection>(FaxDirection.All);
    const [loading, setLoading] = useState(false);

    const apolloClient = useApolloClient();

    const onFilterSubmit = async () => {
        setLoading(true);

        const variables: FaxRecordListQueryVariables = {
            id: faxId,
            interval: getRecordListInterval(interval),
            direction: direction,
        };

        const res = await apolloClient.query<FaxRecordListQuery, FaxRecordListQueryVariables>({
            query: FaxRecordListDocument,
            variables,
            fetchPolicy: "no-cache",
        });

        setRecordList(res.data.faxRecords.records ?? []);

        setLoading(false);
    };

    const onDownload = async () => {
        await downloadRecords(recordList, defaultFieldNames, "fax_records");
        return;
    };

    return (
        <div>
            <FormContainer style={{gridTemplateColumns: "auto 1fr"}}>
                <FormattedFormRow
                    label={defineMessage({id: "faxRecordsList.filters.interval.label", defaultMessage: "Interval"})}
                >
                    <IntervalWidget interval={interval} setInterval={setInterval} disabled={loading} />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "faxRecordsList.filters.direction.label", defaultMessage: "Direction"})}
                >
                    <DirectionWidget direction={direction} setDirection={setDirection} disabled={loading} />
                </FormattedFormRow>
                <FormattedFormRow>
                    <FormattedButton
                        message={defineMessage({id: "search", defaultMessage: "Search"})}
                        onClick={() => onFilterSubmit()}
                        disabled={loading}
                    />
                    <FormattedButton
                        variant="text"
                        message={defineMessage({id: "download", defaultMessage: "Download"})}
                        disabled={loading || recordList == null}
                        style={{marginLeft: 15}}
                        onClick={() => onDownload()}
                    />
                </FormattedFormRow>
            </FormContainer>
            {recordList !== null && (
                <RecordList
                    records={recordList}
                    loading={loading}
                    style={{marginTop: 10}}
                    fields={[
                        {
                            name: "time",
                            label: defineMessage({id: "faxRecordsList.time", defaultMessage: "Time"}),
                        },
                        {
                            name: "remote",
                            label: defineMessage({id: "faxRecordsList.remote", defaultMessage: "Remote"}),
                        },
                        {
                            name: "direction",
                            label: defineMessage({id: "faxRecordsList.direction", defaultMessage: "Direction"}),
                        },
                        {
                            name: "status",
                            label: defineMessage({id: "faxRecordsList.status", defaultMessage: "Status"}),
                        },
                        {
                            name: "pages",
                            label: defineMessage({id: "faxRecordsList.pages", defaultMessage: "Pages"}),
                        },
                        {
                            name: "subject",
                            label: defineMessage({id: "faxRecordsList.subject", defaultMessage: "Subject"}),
                        },
                    ]}
                    generators={fieldContentGenerators}
                />
            )}
        </div>
    );
};
